/**
 * v3 method stage: pure data, copy and geometry (specs/012-manta-character/DESIGN.md §11).
 * No React, no DOM, no clock, so every rule here is unit tested.
 *
 * The story: eight areas sit in a row in exam order. The diagnostic measures them, the
 * rings sort themselves weakest first, the head of the line comes forward to the focus
 * spot where the manta works on it (practice, then the mastery check turns it green),
 * and a proven ring drops into its slot in the score below, which ticks up. A section's
 * subtotal locks once all four of its slots are green. One proven ring is pulled back
 * up once for a quick review. Then the rest, until every area is green.
 */
import { DOMAINS_BY_SECTION, type SatDomain } from '@/api/sat';
import { DIAGNOSTIC_FACTS } from '@/lib/marketingCtas';
import { OPEN_DEMO_MASTERY } from '@/pages/open/openDemoDtos';

// ── data ─────────────────────────────────────────────────────────────────────

export type Section = 'RW' | 'MATH';

/** Exam order: four Reading and Writing, then four Math. */
export const DOMAINS: readonly SatDomain[] = [...DOMAINS_BY_SECTION.READING_AND_WRITING, ...DOMAINS_BY_SECTION.MATH];

export const sectionOf = (i: number): Section => (i < 4 ? 'RW' : 'MATH');
/** Position within its section, 0..3 (the slot it fills in the score). */
export const slotIndexOf = (i: number) => i % 4;

/** The site's example student (openDemoDtos). Weakest: Data Analysis 44, then Conventions 58. */
export const DIAGNOSTIC: Readonly<Record<SatDomain, number>> = OPEN_DEMO_MASTERY;

/** Ring indices weakest first: the order the diagnostic hands out. Ties keep exam order. */
export const RANK_ORDER: readonly number[] = [...DOMAINS.keys()].sort(
  (a, b) => DIAGNOSTIC[DOMAINS[a]] - DIAGNOSTIC[DOMAINS[b]] || a - b,
);

/**
 * The example student's section scores (each out of 800) after the diagnostic and once
 * every area in the section is proven. Chosen by hand: 1090 at the start; 1520 at the end
 * (owner, 2026-10-06), R&W 750 and Math 770, the larger climb in Math where the weakest area
 * was. They illustrate; they are not a prediction and the copy never states a gain. Once
 * both sections lock, a small "and climbing" cue says the course keeps going, without a number.
 */
export const SCORE_START: Readonly<Record<Section, number>> = { RW: 560, MATH: 530 };
export const SCORE_END: Readonly<Record<Section, number>> = { RW: 750, MATH: 770 };

/** The cue after the count settles: no number, no promise. */
export const CLIMB_CUE = 'and climbing';

/**
 * What each proven area adds to its section, in steps of 10 (SAT scores are multiples of
 * 10). The section's gain is split by how far each area started from full (a weaker area
 * has more room), using largest-remainder rounding so the parts sum exactly to the gain.
 */
export function areaGains(
  start = SCORE_START,
  end = SCORE_END,
  diag: Readonly<Record<SatDomain, number>> = DIAGNOSTIC,
): Record<SatDomain, number> {
  const out = {} as Record<SatDomain, number>;
  for (const section of ['RW', 'MATH'] as const) {
    const idx = [0, 1, 2, 3].map((k) => (section === 'RW' ? k : k + 4));
    const gaps = idx.map((i) => Math.max(0, 100 - diag[DOMAINS[i]]));
    const gapSum = gaps.reduce((a, b) => a + b, 0) || 1;
    const tens = Math.round((end[section] - start[section]) / 10);
    const raw = gaps.map((g) => (g / gapSum) * tens);
    const base = raw.map(Math.floor);
    let left = tens - base.reduce((a, b) => a + b, 0);
    const byRemainder = raw.map((r, k) => ({ k, rem: r - Math.floor(r) })).sort((a, b) => b.rem - a.rem || a.k - b.k);
    for (const { k } of byRemainder) {
      if (left <= 0) break;
      base[k] += 1;
      left -= 1;
    }
    idx.forEach((i, k) => (out[DOMAINS[i]] = base[k] * 10));
  }
  return out;
}

export const AREA_GAINS: Readonly<Record<SatDomain, number>> = areaGains();

export interface ScoreState {
  rw: number;
  math: number;
  total: number;
  /** All four areas of the section are proven: the subtotal is final. */
  rwLocked: boolean;
  mathLocked: boolean;
}

/** The score for a set of proven ring indices. Only proven areas count; practice never moves it. */
export function scoreFor(proven: Iterable<number>): ScoreState {
  const set = new Set(proven);
  let rw = SCORE_START.RW;
  let math = SCORE_START.MATH;
  let rwN = 0;
  let mathN = 0;
  for (const i of set) {
    if (i < 0 || i > 7) continue;
    if (sectionOf(i) === 'RW') {
      rw += AREA_GAINS[DOMAINS[i]];
      rwN++;
    } else {
      math += AREA_GAINS[DOMAINS[i]];
      mathN++;
    }
  }
  return { rw, math, total: rw + math, rwLocked: rwN === 4, mathLocked: mathN === 4 };
}

// ── ring model ───────────────────────────────────────────────────────────────

export type Phase = 'unknown' | 'revealed' | 'practising' | 'proven' | 'reviewed';

export interface RingModel {
  phase: Phase;
  /** Arc shown, 0..100. null while unknown. */
  shown: number | null;
  reps: number;
  repsTotal: number;
  /** Bumps to replay the review pulse. */
  pulse: number;
}

export const isProven = (r: RingModel) => r.phase === 'proven' || r.phase === 'reviewed';

export function freshRings(): RingModel[] {
  return DOMAINS.map(() => ({ phase: 'unknown', shown: null, reps: 0, repsTotal: 0, pulse: 0 }));
}

/** Practice climbs the arc to here; the mastery check completes it. */
export const PRACTICE_TOP = 88;

/** Arc after rep `k` of `reps`, from where the ring started. */
export const repShown = (start: number, k: number, reps: number) => Math.round(start + ((PRACTICE_TOP - start) * k) / reps);

// ── copy ─────────────────────────────────────────────────────────────────────

export type Beat = 'start' | 'diagnose' | 'sort' | 'first' | 'practice' | 'prove' | 'next' | 'review' | 'rest' | 'done';

/**
 * One caption per beat, plain sentences, no dashes. The manta is named once ("Mentara")
 * as the actor; after that "it". The score is never described: it is labelled as an
 * example and left to speak.
 */
export const CAPTIONS: Readonly<Record<Beat, string>> = {
  start: 'Eight areas. None measured yet.',
  diagnose: `A ${DIAGNOSTIC_FACTS.questions}-question diagnostic finds your weak spots.`,
  sort: 'Mentara lines them up, weakest first.',
  first: 'It starts where you are weakest.',
  practice: 'Every practice set aims there.',
  prove: 'A mastery check turns it green.',
  next: 'Then the next weakest.',
  review: 'Green areas come back for a quick review.',
  rest: 'Until every area is green.',
  done: 'Every area green.',
};

// ── timing (ms at speed 1) ───────────────────────────────────────────────────

export const TIMING = {
  startHoldMs: 300,
  /** The scan: from the work spot down to the start of the line, then along it. */
  scanMs: 1750,
  sortMs: 660,
  /** A ring rising from the line to the focus spot (towed or on its own). */
  riseMs: 600,
  /** The manta's hop to pick up the head of the line, and back with it. */
  towHopMs: 480,
  repMs: 300,
  repsFirst: 3,
  repsSecond: 2,
  /** A proven ring dropping into its slot; the score ticks on landing. */
  dropMs: 580,
  /** The first two proven rings hold at the focus spot with their flag before they drop. */
  flagHoldMs: 260,
  tickMs: 650,
  reviewHoldMs: 360,
  /** The last six areas run at this tempo multiplier. */
  restBoost: 2.8,
  /** Hero autoplay delay once the stage is at least half in view. */
  autoplayDelayMs: 500,
} as const;

// ── geometry ─────────────────────────────────────────────────────────────────

export interface Pt {
  x: number;
  y: number;
}

export interface StageLayout {
  id: 'desktop' | 'mobile';
  vb: { w: number; h: number };
  /** Rings are drawn at this radius and scaled to their place. */
  baseR: number;
  /** The line: exam-order positions before the sort, then the queue. */
  rowY: number;
  rowX: readonly number[];
  rowR: number;
  queueX: (k: number) => number;
  queueR: number;
  focus: Pt & { r: number };
  /** Where the focused area's name sits, and how it is anchored. */
  focusLabel: Pt & { anchor: 'start' | 'middle' };
  slot: (i: number) => Pt;
  slotR: number;
  /** Score typography anchors. */
  score: {
    label: Pt;
    total: Pt & { size: number };
    rows: Record<Section, { label: Pt; value: Pt & { size: number } }>;
    /** Line under the four slots that draws when the section locks. */
    lockLine: Record<Section, { x1: number; x2: number; y: number }>;
    /** Where the "and climbing" cue sits once both sections lock (start of its chevron, text baseline). */
    climb: Pt;
  };
  sectionLabels: Record<Section, Pt>;
  /** The character's width in stage units. */
  mantaW: number;
  labelFont: number;
}

/** The body's anchor sits this far into the character's width from its left edge (520/744). */
const ANCHOR_X = 520 / 744;

/**
 * Desktop and tablet: 640 x 440. The line at y 214, the focus spot raised above it in the
 * middle, the score under a band of whitespace. The manta works from the focus spot's left.
 */
export const DESKTOP: StageLayout = (() => {
  const rowX = [0, 1, 2, 3, 4, 5, 6, 7].map((i) => 52 + i * 70 + (i >= 4 ? 26 : 0));
  const slotX = (k: number) => 404 + k * 44;
  return {
    id: 'desktop',
    vb: { w: 640, h: 440 },
    baseR: 40,
    rowY: 214,
    rowX,
    rowR: 19,
    queueX: (k) => 52 + k * 78,
    queueR: 18,
    focus: { x: 330, y: 100, r: 40 },
    focusLabel: { x: 386, y: 96, anchor: 'start' },
    slot: (i) => ({ x: slotX(slotIndexOf(i)), y: sectionOf(i) === 'RW' ? 320 : 396 }),
    slotR: 14,
    score: {
      label: { x: 0, y: 300 },
      total: { x: 0, y: 356, size: 48 },
      rows: {
        RW: { label: { x: 236, y: 310 }, value: { x: 236, y: 336, size: 22 } },
        MATH: { label: { x: 236, y: 386 }, value: { x: 236, y: 412, size: 22 } },
      },
      lockLine: {
        RW: { x1: slotX(0), x2: slotX(3), y: 346 },
        MATH: { x1: slotX(0), x2: slotX(3), y: 422 },
      },
      climb: { x: 2, y: 382 },
    },
    sectionLabels: { RW: { x: (rowX[0] + rowX[3]) / 2, y: 256 }, MATH: { x: (rowX[4] + rowX[7]) / 2, y: 256 } },
    mantaW: 104,
    labelFont: 12,
  };
})();

/**
 * Phones: 390 x 392, drawn edge to edge and kept short so the line and the total are on the
 * first screen under the CTA. The focus spot sits above the line right of centre (the manta
 * works from its left, its name goes under it), the score stacks under the line.
 */
export const MOBILE: StageLayout = (() => {
  const rowX = [0, 1, 2, 3, 4, 5, 6, 7].map((i) => 30 + i * 44 + (i >= 4 ? 10 : 0));
  const slotX = (k: number) => 244 + k * 38;
  return {
    id: 'mobile',
    vb: { w: 390, h: 392 },
    baseR: 40,
    rowY: 168,
    rowX,
    rowR: 15,
    queueX: (k) => 30 + k * 50,
    queueR: 14,
    focus: { x: 232, y: 64, r: 30 },
    focusLabel: { x: 274, y: 60, anchor: 'start' },
    slot: (i) => ({ x: slotX(slotIndexOf(i)), y: sectionOf(i) === 'RW' ? 296 : 356 }),
    slotR: 12,
    score: {
      label: { x: 16, y: 228 },
      total: { x: 16, y: 268, size: 40 },
      rows: {
        RW: { label: { x: 16, y: 301 }, value: { x: 150, y: 302, size: 18 } },
        MATH: { label: { x: 16, y: 361 }, value: { x: 150, y: 362, size: 18 } },
      },
      lockLine: {
        RW: { x1: slotX(0), x2: slotX(3), y: 318 },
        MATH: { x1: slotX(0), x2: slotX(3), y: 378 },
      },
      climb: { x: 168, y: 268 },
    },
    sectionLabels: { RW: { x: (rowX[0] + rowX[3]) / 2, y: 202 }, MATH: { x: (rowX[4] + rowX[7]) / 2, y: 202 } },
    mantaW: 84,
    labelFont: 11,
  };
})();

export interface StageState {
  rings: readonly RingModel[];
  /** The diagnostic has handed out the order: the line shows the queue, not exam order. */
  sorted: boolean;
  /** The ring at the focus spot (being worked on, or pulled back for review). */
  focus: number | null;
}

export type Place = 'row' | 'queue' | 'focus' | 'slot';

export interface Placement extends Pt {
  r: number;
  place: Place;
}

/** The queue: unproven rings weakest first, without the focused one. */
export function queueOf(s: StageState): number[] {
  return RANK_ORDER.filter((i) => i !== s.focus && !isProven(s.rings[i]));
}

/**
 * Where ring `i` sits and how big it is. The motion grammar is in the transitions between
 * these places: row to queue is the sort, queue to focus is "comes forward", focus to slot
 * is "settles into the score", slot to focus and back is the review.
 */
export function placeRing(i: number, s: StageState, L: StageLayout): Placement {
  if (s.focus === i) return { x: L.focus.x, y: L.focus.y, r: L.focus.r, place: 'focus' };
  if (isProven(s.rings[i])) return { ...L.slot(i), r: L.slotR, place: 'slot' };
  if (s.sorted) {
    const k = queueOf(s).indexOf(i);
    return { x: L.queueX(Math.max(0, k)), y: L.rowY, r: L.queueR, place: 'queue' };
  }
  return { x: L.rowX[i], y: L.rowY, r: L.rowR, place: 'row' };
}

// ── where the manta goes ─────────────────────────────────────────────────────

/** The working pose: left of the focus spot, facing it, wing tip just short of the ring. */
export function focusAnchor(L: StageLayout): Pt {
  return { x: L.focus.x - L.focus.r - (1 - ANCHOR_X) * L.mantaW - 10, y: L.focus.y - 0.06 * L.mantaW };
}

/** Hovering just above a ring on the line (scan pass, picking up the head of the queue). */
export function aboveAnchor(L: StageLayout, at: Pt, r: number): Pt {
  return { x: at.x - 0.08 * L.mantaW, y: at.y - r - 0.3 * L.mantaW };
}

/** The scan pass runs above the whole line, from just before the first ring to just past the last. */
export function scanEnds(L: StageLayout): { from: Pt; to: Pt } {
  const y = L.rowY - L.rowR - 0.3 * L.mantaW;
  return { from: { x: L.rowX[0] - 0.1 * L.mantaW, y }, to: { x: L.rowX[7] + 0.05 * L.mantaW, y } };
}

/**
 * Resting pose. Before the run: the work spot, left of the (empty) focus spot, so the first
 * frame has the character at the top of the stage looking over the line. After it: `doneAnchor`.
 */
export function restAnchor(L: StageLayout, done = false): Pt {
  return done ? doneAnchor(L) : focusAnchor(L);
}

/** After the last area: over the empty line, above the total, presenting the score. */
export function doneAnchor(L: StageLayout): Pt {
  return { x: L.id === 'mobile' ? L.vb.w * 0.5 : L.vb.w * 0.3, y: L.rowY - 0.12 * L.mantaW };
}

/** The scan pass as one path: from where the manta is, down to the start of the line, then along it. */
export function scanPathD(L: StageLayout, from: Pt): string {
  const { from: a, to: b } = scanEnds(L);
  const c1 = { x: from.x - 0.25 * (from.x - a.x), y: from.y + 0.15 * (a.y - from.y) };
  const c2 = { x: a.x - 0.6 * L.mantaW, y: a.y - 0.02 * L.mantaW };
  const lead = { x: a.x + 0.5 * L.mantaW, y: a.y };
  return `M ${r1(from.x)} ${r1(from.y)} C ${r1(c1.x)} ${r1(c1.y)}, ${r1(c2.x)} ${r1(c2.y)}, ${r1(a.x)} ${r1(a.y)} C ${r1(lead.x)} ${r1(lead.y)}, ${r1(b.x - 0.5 * L.mantaW)} ${r1(b.y)}, ${r1(b.x)} ${r1(b.y)}`;
}

/**
 * One hop as an SVG path: a cubic from `from` to `to` that arcs up a little (a manta glides,
 * it does not slide), and leaves along `heading` when the character is already moving so an
 * interrupted hop bends instead of kinking.
 */
export function hopPathD(from: Pt, to: Pt, heading: { dx: number; dy: number } | null = null, liftOverride?: number): string {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dist = Math.hypot(dx, dy);
  if (dist < 0.5) return `M ${r1(from.x)} ${r1(from.y)} L ${r1(from.x + 0.5)} ${r1(from.y)}`;
  const lift = liftOverride ?? Math.min(48, dist * 0.22);
  const c1 = heading
    ? { x: from.x + heading.dx * dist * 0.35, y: from.y + heading.dy * dist * 0.35 }
    : { x: from.x + dx * 0.3, y: from.y + dy * 0.3 - lift };
  const c2 = { x: to.x - dx * 0.3, y: to.y - dy * 0.3 - lift * 0.6 };
  return `M ${r1(from.x)} ${r1(from.y)} C ${r1(c1.x)} ${r1(c1.y)}, ${r1(c2.x)} ${r1(c2.y)}, ${r1(to.x)} ${r1(to.y)}`;
}

const r1 = (v: number) => Math.round(v * 10) / 10;
